import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Radio,
  Stack,
  CircularProgress,
} from "@mui/material";
import axios from "../../../config/axiosConfig";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import { useAuth } from "../../../context/AuthContext";

// Mismo patrón que EmparejarEquiposOT (TKR: Radio↔Batería), pero para AVL/Scada:
// Módem↔Chip. Cierra un hueco que existía desde antes (ver comentario en
// OrdenTrabajoPaso.jsx junto a EmparejarEquiposOT: "el emparejamiento Modem↔Chip de
// ajustar.php para AVL/Scada no está cubierto todavía"). Se elige UN módem y se le
// relacionan uno o más chips. Solo actualiza equipo_chip.relacion (+
// historial_chip.relacion cuando el chip viene del historial de esta OT).
const headCellSx = {
  fontWeight: 800,
  bgcolor: (t) => (t.palette.mode === "dark" ? t.palette.background.paper : t.palette.grey[100]),
};
const tableSx = { "& .MuiTableCell-root": { py: 0.25, px: 0.6, fontSize: 11 } };

// Un bloque de emparejamiento (módem + chips) con su propia selección y guardado.
function BloqueEmparejamiento({ titulo, modems, chips, onEmparejado }) {
  const [modemSeleccionado, setModemSeleccionado] = useState("");
  const [chipsSeleccionados, setChipsSeleccionados] = useState([]);
  const [guardando, setGuardando] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const toggleChip = (id) => {
    setChipsSeleccionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const emparejar = async () => {
    if (!modemSeleccionado || !chipsSeleccionados.length) return;
    setGuardando(true);
    try {
      for (const id of chipsSeleccionados) {
        const chip = chips.find((c) => c.id === id);
        if (!chip) continue;
        await axios.put(`/api/equipos-chip/${id}`, { relacion: modemSeleccionado });
        if (chip.id_historial) {
          await axios.put(`/api/historial-chip/${chip.id_historial}`, { relacion: modemSeleccionado });
        }
      }
      setModemSeleccionado("");
      setChipsSeleccionados([]);
      setOpenSnackbar(true);
      onEmparejado();
    } catch (err) {
      console.error("Error al emparejar equipos:", err);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
          {titulo}
        </Typography>
        <Button
          size="small"
          variant="contained"
          color="success"
          disabled={!modemSeleccionado || !chipsSeleccionados.length || guardando}
          onClick={emparejar}
          sx={{ minHeight: 26, height: 26, fontSize: 11, whiteSpace: "nowrap" }}
        >
          {guardando ? <CircularProgress size={16} color="inherit" /> : "Emparejar Seleccionados"}
        </Button>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Módem
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 34 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Num. SIM</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!modems.length ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        No hay módems en este grupo.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  modems.map((m) => (
                    <TableRow
                      key={m.num_sim}
                      hover
                      selected={modemSeleccionado === m.num_sim}
                      onClick={() => setModemSeleccionado(m.num_sim)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Radio size="small" checked={modemSeleccionado === m.num_sim} onChange={() => setModemSeleccionado(m.num_sim)} sx={{ p: 0 }} />
                      </TableCell>
                      <TableCell>{m.num_sim}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box sx={{ flex: 1.4, minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Chips
          </Typography>
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 220, borderRadius: 0.5 }}>
            <Table size="small" stickyHeader sx={tableSx}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: 34 }}>Sel.</TableCell>
                  <TableCell sx={headCellSx}>Número</TableCell>
                  <TableCell sx={headCellSx}>Modelo</TableCell>
                  <TableCell sx={headCellSx}>Relación actual</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!chips.length ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                        No hay chips en este grupo.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  chips.map((c) => {
                    const checked = chipsSeleccionados.includes(c.id);
                    return (
                      <TableRow key={c.id} hover selected={checked}>
                        <TableCell>
                          <Checkbox size="small" checked={checked} onChange={() => toggleChip(c.id)} sx={{ p: 0 }} />
                        </TableCell>
                        <TableCell>{c.numero}</TableCell>
                        <TableCell>{c.modelo}</TableCell>
                        <TableCell>{c.relacion || "—"}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Stack>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity="success" variant="filled" sx={{ width: "100%" }}>
          Chips emparejados correctamente.
        </MuiAlert>
      </Snackbar>
    </Paper>
  );
}

// estadoNuevo/estadoActivo: literales de estado a filtrar en historial_modem/
// equipo_modem (y su equivalente en chip) respectivamente. Se pasan distintos según el
// flujo que reutilice este componente (Activar Equipos Vendidos usa "Asignado
// Vendido"/"Vendido Activo").
// razonActiva (opcional): filtra el lado "Equipos del Cliente" de los CHIPS por
// equipo_chip.razon — mismo criterio que EmparejarEquiposOT (usado por la OT de
// Prueba). El lado Módem no se filtra (mismo motivo: legacy tampoco ofrece un pool de
// módems "a prueba" del cliente, solo el de esta OT).
const EmparejarModemChipOT = ({ cod_ot, cod_servicio_cliente, estadoNuevo = "Asignado", estadoActivo = "Activo", razonActiva }) => {
  const { user } = useAuth();
  const [modems, setModems] = useState([]);
  const [chips, setChips] = useState([]);
  const [loading, setLoading] = useState(true);

  const cargar = () => {
    setLoading(true);
    // Solo equipo Activo de la sucursal del usuario logueado — mismo criterio
    // que DevolucionEquiposOT/SeleccionarMantenimientoOT. El equipo del
    // historial de esta OT no se filtra: ya está asignado a esta OT.
    const ciudad = user?.sucursal || undefined;
    const paramsChipActivo = { estado: estadoActivo, cod_servicio: cod_servicio_cliente, ciudad };
    if (razonActiva) paramsChipActivo.razon = razonActiva;
    Promise.all([
      axios.get(`/api/historial-modem`, { params: { cod_ot } }),
      axios.get(`/api/equipos-modem`, { params: { estado: estadoActivo, cod_servicio: cod_servicio_cliente, ciudad } }),
      axios.get(`/api/historial-chip`, { params: { cod_ot } }),
      axios.get(`/api/equipos-chip`, { params: paramsChipActivo }),
    ])
      .then(([resModemHist, resModemActivos, resChipHist, resChipActivos]) => {
        const modemsNuevos = (resModemHist.data || [])
          .filter((m) => m.estado === estadoNuevo)
          .map((m) => ({ num_sim: m.num_sim, origen: "Nueva" }));
        const modemsActivos = (resModemActivos.data?.rows || resModemActivos.data || []).map((m) => ({ num_sim: m.num_sim, origen: "Activa" }));
        const simsVistos = new Set();
        const modemsCombinados = [...modemsNuevos, ...modemsActivos].filter((m) => {
          if (simsVistos.has(m.num_sim)) return false;
          simsVistos.add(m.num_sim);
          return true;
        });
        setModems(modemsCombinados);

        const chipsNuevos = (resChipHist.data || [])
          .filter((c) => c.estado === estadoNuevo)
          .map((c) => ({
            id: c.equipo_id,
            id_historial: c.id_historial,
            numero: c.numero,
            modelo: c.modelo,
            relacion: c.relacion_actual,
            origen: "Nueva",
          }));
        const chipsActivos = (resChipActivos.data?.rows || resChipActivos.data || []).map((c) => ({
          id: c.ID,
          id_historial: null,
          numero: c.numero,
          modelo: c.modelo,
          relacion: c.relacion,
          origen: "Activa",
        }));
        const idsVistos = new Set();
        const chipsCombinados = [...chipsNuevos, ...chipsActivos].filter((c) => {
          if (!c.id || idsVistos.has(c.id)) return false;
          idsVistos.add(c.id);
          return true;
        });
        setChips(chipsCombinados);
      })
      .catch((err) => console.error("Error al cargar módems/chips para emparejar", err))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(cargar, [cod_ot, cod_servicio_cliente, estadoNuevo, estadoActivo, razonActiva]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const modemsOT = modems.filter((m) => m.origen === "Nueva");
  const chipsOT = chips.filter((c) => c.origen === "Nueva");
  const modemsCliente = modems.filter((m) => m.origen === "Activa");
  const chipsCliente = chips.filter((c) => c.origen === "Activa");

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction={{ xs: "column", lg: "row" }} spacing={1.5} alignItems="stretch">
        <BloqueEmparejamiento
          titulo="Equipos de la OT"
          modems={modemsOT}
          chips={chipsOT}
          onEmparejado={cargar}
        />
        <BloqueEmparejamiento
          titulo="Equipos del Cliente"
          modems={modemsCliente}
          chips={chipsCliente}
          onEmparejado={cargar}
        />
      </Stack>
    </Box>
  );
};

export default EmparejarModemChipOT;
